import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, ExternalLink, SkipForward } from 'lucide-react';
import type { AuditoriaCompleteRequest, ItemDTO, ItemTipo } from '@central/types';
import {
  useAuditoriaComplete,
  useAuditoriaPending,
  useAuditoriaSkip,
  useAuditoriaStats,
} from '@/hooks/useAuditoria';
import { useItemsList } from '@/hooks/useItems';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { TIPO_LABELS } from '@/components/ItemStatusBadge';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

type Status = 'em-producao' | 'pausado' | 'arquivado';

export function AuditoriaPage(): JSX.Element {
  const { data: pendingData, isLoading } = useAuditoriaPending();
  const { data: stats } = useAuditoriaStats();
  const { data: mentoradasData } = useItemsList({ tipo: 'mentorada' });
  const complete = useAuditoriaComplete();
  const skip = useAuditoriaSkip();
  const accessToken = useAuthStore((s) => s.accessToken);

  const items = useMemo(() => pendingData?.items ?? [], [pendingData]);
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    if (cursor >= items.length && items.length > 0) setCursor(items.length - 1);
  }, [items.length, cursor]);

  const current: ItemDTO | undefined = items[cursor];

  // Estado do formulário atual
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [tagsStr, setTagsStr] = useState('');
  const [mentoradaId, setMentoradaId] = useState<string>('');
  const [status, setStatus] = useState<Status>('em-producao');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!current) return;
    setDescricao(current.descricao ?? '');
    setCategoria(current.categoria ?? '');
    setTagsStr(current.tags.join(', '));
    setMentoradaId(current.mentorada_id ? String(current.mentorada_id) : '');
    setStatus('em-producao');
  }, [current?.id]);

  async function handleComplete(): Promise<void> {
    if (!current) return;
    setSaving(true);
    const payload: AuditoriaCompleteRequest = {
      descricao: descricao || undefined,
      categoria: categoria || undefined,
      tags: tagsStr
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      mentorada_id: mentoradaId ? Number(mentoradaId) : null,
      status,
    };
    try {
      await complete.mutateAsync({ id: current.id, payload });
      // Cursor: próxima ficha pendente após remoção desta
      if (cursor >= items.length - 1) setCursor(Math.max(items.length - 2, 0));
    } finally {
      setSaving(false);
    }
  }

  async function handleSkip(): Promise<void> {
    if (!current) return;
    await skip.mutateAsync(current.id);
    if (cursor < items.length - 1) setCursor(cursor + 1);
  }

  function goPrev(): void {
    setCursor(Math.max(0, cursor - 1));
  }

  function goNext(): void {
    setCursor(Math.min(items.length - 1, cursor + 1));
  }

  // Atalhos de teclado
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) {
        return;
      }
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void handleComplete();
      } else if (e.key === 's' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void handleSkip();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cursor, items.length, descricao, categoria, tagsStr, mentoradaId, status]);

  async function handleExport(): Promise<void> {
    const res = await fetch('/api/auditoria/export', {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken ?? ''}` },
    });
    if (!res.ok) {
      alert('Falha ao exportar: ' + res.status);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `apendice-a-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Sem pendentes
  if (!isLoading && items.length === 0) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-semibold">Auditoria</h1>
          <p className="text-sm text-muted-foreground">Todas as fichas estão revisadas 🎉</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="text-6xl mb-3">🎉</div>
              <h2 className="text-xl font-semibold mb-1">Auditoria em dia</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Quando a varredura detectar itens novos, eles aparecem aqui automaticamente.
              </p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={handleExport}>
                  <Download className="h-4 w-4" /> Exportar Apêndice A
                </Button>
                <Link to="/">
                  <Button variant="ghost">Voltar ao Dashboard</Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <nav className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Dashboard</Link> › Auditoria
          </nav>
          <h1 className="text-2xl font-semibold">Auditoria</h1>
          <p className="text-sm text-muted-foreground">
            Preencha a ficha de cada item detectado. Use <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">J/K</kbd> ou setas pra navegar, <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">⌘↵</kbd> pra completar, <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">⌘S</kbd> pra pular.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4" /> Exportar Apêndice A
        </Button>
      </div>

      {/* Progress bar */}
      {stats && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="font-medium">
                Progresso:{' '}
                <span className="text-foreground">{stats.total - stats.pendentes}</span>
                <span className="text-muted-foreground"> / {stats.total}</span>
              </span>
              <span className="text-muted-foreground">{stats.percentual}%</span>
            </div>
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${stats.percentual}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-muted-foreground">
              {Object.entries(stats.por_tipo).map(([tipo, v]) => (
                <span key={tipo}>
                  {TIPO_LABELS[tipo] ?? tipo}: {v.pendentes}/{v.total}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Item atual */}
      {current && (
        <Card>
          <CardContent className="pt-4 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{TIPO_LABELS[current.tipo] ?? current.tipo}</Badge>
                  <Badge variant="secondary" className="text-[10px]">{current.origem}</Badge>
                </div>
                <h2 className="text-xl font-semibold">{current.nome}</h2>
                {current.url && (
                  <a
                    href={current.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {current.url}
                  </a>
                )}
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <div>
                  {cursor + 1} de {items.length}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium mb-1">
                  Propósito / Descrição
                </label>
                <Textarea
                  id="descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="O que esse item é e pra que serve"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="categoria" className="block text-sm font-medium mb-1">
                    Categoria
                  </label>
                  <Input
                    id="categoria"
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                    placeholder="Ex: Infraestrutura, Vendas, Tracking"
                  />
                </div>
                <div>
                  <label htmlFor="mentorada" className="block text-sm font-medium mb-1">
                    Mentorada-dona (se aplicável)
                  </label>
                  <select
                    id="mentorada"
                    value={mentoradaId}
                    onChange={(e) => setMentoradaId(e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">— Nenhuma —</option>
                    {(mentoradasData?.items ?? []).map((m) => (
                      <option key={m.id} value={m.id}>{m.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium mb-1">
                    Tags (vírgula)
                  </label>
                  <Input
                    id="tags"
                    value={tagsStr}
                    onChange={(e) => setTagsStr(e.target.value)}
                    placeholder="producao, api, leads"
                  />
                </div>
                <div>
                  <label htmlFor="status" className="block text-sm font-medium mb-1">
                    Status ao marcar como revisado
                  </label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as Status)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="em-producao">Em produção</option>
                    <option value="pausado">Pausado</option>
                    <option value="arquivado">Arquivado</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={goPrev} disabled={cursor === 0}>
                  ← Anterior
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={goNext}
                  disabled={cursor >= items.length - 1}
                >
                  Próximo →
                </Button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleSkip}>
                  <SkipForward className="h-4 w-4" /> Pular
                </Button>
                <Button size="sm" onClick={handleComplete} disabled={saving}>
                  <Check className="h-4 w-4" /> {saving ? 'Salvando…' : 'Completar'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
